import moment from 'moment'
import cookie, { AHA_TOKEN_COOKIE, AHA_LOGGED_IN_COOKIE } from '@/services/cookie'
import sib from '@/services/sib'
import zoho from '@/services/zoho'
import mixpanelUtils from '@/utils/mixpanel.utils'

import UserApi from '@/api/user'
import ABTestApi from '@/api/abTest'
import AccountApi from '@/api/account'
import utils from '@/util'

import constants from '@/constant'
import PlanAPI from '@/api/plan'
import PlanBenefitAPI from '@/api/planBenefit'
import Sentry from '@/services/sentry'
import store from '@/vuex/store'
import EventBus from '@/eventBus'
import UnleashService from '@/services/unleash'
import { flags } from '@/utils/abtest.utils'
import { knownFlagCohortsOf, startFlagCohortRecording } from '@/business-logic/flag-cohort-recording'

const { eventCategories, AB_TEST } = constants
const userIdcookieName = 'aha-user-id'
let retryGetPlanBenefitTimes = 0
const API_CACHE_MAX_AGE_SECONDS = 3

// Since the AHA-40764 cookie-auth cutover (~Aug 20 2026, JS Bearer token -> httpOnly
// `ahaToken` cookie), a 401 from /api/pricing/my-plan/ is EXPECTED for a user who never
// had an active session (guest/free viewer, or a signed-out visitor) — the endpoint is
// called unconditionally on load, logged in or not. That case is not a fault and must
// not consume an error-level Sentry slot. A 401 while `ahaLoggedIn` IS set is different:
// it means the session cookie exists but the httpOnly `ahaToken` was not sent — the
// actual regression this cutover can produce — so it must keep surfacing as an error.
const isExpectedMyPlanFailure = (status, hasLoggedInCookie) => (
  (status === 401 && !hasLoggedInCookie) ||
  status === 0
)

const MY_PLAN_ERROR_MESSAGE = 'Error get my plan subscription in current user file'

const reportMyPlanError = (e, source) => {
  // Runs inside a catch block, so it must never throw itself — a broken debug helper
  // would be worse than the bug we are chasing. Fall back to the original bare capture.
  try {
    const status = e && typeof e.status !== 'undefined' ? e.status : 'none'
    const online = typeof navigator !== 'undefined' ? navigator.onLine : null
    const hasLoggedInCookie = !!cookie.getValue(AHA_LOGGED_IN_COOKIE)
    const tags = {
      my_plan_status: String(status),
      my_plan_online: String(online),
      my_plan_has_logged_in_cookie: String(hasLoggedInCookie),
      my_plan_source: source,
    }

    if (isExpectedMyPlanFailure(status, hasLoggedInCookie)) {
      // Downgrade instead of dropping outright: keep one low-noise breadcrumb so an
      // actual spike in this "expected" bucket is still visible in a session replay
      // or a future metric, without occupying the error-level issue.
      Sentry.addBreadcrumb({
        category: 'my-plan',
        message: 'Error get my plan subscription in current user file (expected)',
        level: 'info',
        data: tags,
      })
      return
    }

    // The fingerprint keeps these events in one issue whatever stack the wrapped string
    // carries. `{ tags, extra }` is detected as scope context (both keys are ScopeContext
    // keys) and merged onto the event, so the tag breakdown lands on that same issue.
    Sentry.captureException(
      MY_PLAN_ERROR_MESSAGE,
      {
        fingerprint: [MY_PLAN_ERROR_MESSAGE],
        tags,
        extra: {
          source,
          status,
          statusText: e && e.statusText,
          url: e && e.url,
          online,
          hasLoggedInCookie,
          errorName: e && e.name,
          errorMessage: e && e.message,
        },
      },
    )
  } catch (reportErr) {
    Sentry.captureException(e, { extra: { message: MY_PLAN_ERROR_MESSAGE } })
  }
}

const getUserPlanBenefit = async (currentUser, state) => {
  try {
    const resp = await PlanBenefitAPI.getUserPlanBenefit()
    state.planBenefit = resp
    if (currentUser.plan.indexOf(state.planBenefit.name) === -1 && currentUser.plan.indexOf('edu') === -1) {
      Sentry.captureException(new Error('PlanBenefit mismatch'), {
        extra: {
          planBenefit: state.planBenefit,
          user: {
            plan: currentUser.plan,
            id: currentUser.id,
          },
        },
      })
    }
  } catch (error) {
    if (retryGetPlanBenefitTimes < 10 && error && error.status) {
      retryGetPlanBenefitTimes += 1
      getUserPlanBenefit(currentUser, state)
    }
    Sentry.captureException(new Error('getUserPlanBenefit error'), {
      extra: {
        planBenefit: state.planBenefit,
        user: {
          plan: currentUser.plan,
          id: currentUser.id,
        },
        error,
      },
    })
  }
}

// AHA-42054: resolve the shared pricing lineup (safe-default on failure) and only
// then fetch plan benefit, so the no-essential override (rewriting server
// `*_planName` hints) reads the right value. Extracted as a named helper so the
// caller chains a single function instead of nesting a promise in `.then`.
const fetchLineupThenPlanBenefit = async (currentUser, state) => {
  await store.dispatch('pricingLineup/fetchPricingLineup').catch(() => {})
  return getUserPlanBenefit(currentUser, state)
}

const setCurrentUserAction = async ({ commit, state }, payload) => {
  // AHA-46714: leaving a LOADED identity — switching accounts, or the {} wipe
  // in the /pages/token re-auth flow — drops the recorded editor Back target;
  // a pre-identity (null-userId) value would otherwise be accepted for the
  // next account (see resolveBackDestination in editor.module). First profile
  // load and a same-user refresh must NOT clear. Callers pass the full user
  // object: a partial payload without id counts as leaving the identity.
  try {
    const previousUserId = state.currentUser && state.currentUser.id
    const nextUserId = payload && payload.id
    if (previousUserId != null && nextUserId !== previousUserId) {
      commit('presentationEditor/SET_LAST_DESTINATION', null, { root: true })
    }
  } catch (error) {
    // Best-effort: clearing the Back target must never block the identity commit below.
  }
  commit('setCurrentUser', payload)
  try {
    if (!state.planBenefit.name && state.currentUser.id) {
      getUserPlanBenefit(state.currentUser, state)
    }
  } catch (error) {
    // do nothing
  }
}

const setPlan = async ({ commit }, payload) => {
  commit('setPlan', payload)
}

const updateCurrentABTestAction = async ({ commit }, payload) => {
  commit('updateCurrentABTest', payload)
}

const callApiGetABTest = async (abTestCode) => {
  try {
    return ABTestApi.getTest(abTestCode)
  } catch (e) {
    Sentry.captureException(e)
    return false
  }
}

const updateUserAfterCreatingTeamAction = async ({ commit, state }) => {
  const { currentUser } = state
  const user = await UserApi.getUser()
  if (user) {
    const patchedUser = {
      ...currentUser,
      ...user,
    }
    commit('updateCurrentUser', patchedUser)
  }
}

const checkUserLanguage = (currentUser, commit) => {
  try {
    const storedLanguageCode = window.localStorage.getItem('ahaslides-presenter-language')
    const presenterLanguage = currentUser.personalInformation && currentUser.personalInformation.presenterLanguage
    const languageCode = presenterLanguage || storedLanguageCode || 'en'
    commit('setPresenterLang', languageCode)
    window.localStorage.setItem('ahaslides-presenter-language', languageCode)
  } catch (error) {
    Sentry.captureException(error)
  }
}

const handleUnleashStart = async (currentUser, state) => {
  await store.dispatch('featureToggles/initializeUserFeatureContext', {
    currentUser,
    presenterLanguage: state.presenterLocale,
  })
  store.dispatch('featureToggles/startUnleash')
  // eslint-disable-next-line sonarjs/no-duplicate-string -- the flag-convention checker only recognises the literal getter path
  if (store.getters['featureToggles/canUseFlagCohorts']) {
    startFlagCohortRecording(store, UserApi).catch(() => {})
  }
}

let isFirstTimeLoadService = false

/**
 * @deprecated
 *
 * moving to use fetchCurrentUserAction
 * keeping this function here for fallback purpose
 * will remove getCurrentUserActionV1 is fully rolled out
 */
const getCurrentUserActionV1 = async ({
  commit, state, dispatch,
}) => {
  let { currentUser } = state
  try {
    if (UnleashService) {
      UnleashService.start()
      await UnleashService.ready
    }
  } catch (error) {
    // if Unleash failed to load here, it should not affect the flow to get user profile.
    // so do nothing here
  }
  try {
    // phải lấy profile 2 lần nếu k thì k thì có bug khi signup từ form ở trang chủ
    // http://localhost:8080/pages/token?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MzU
    // sImlhdCI6MTYzMjIxODAyNiwiZXhwIjoxNjk1MjkwMDI2fQ.dOpl8ICE7i-2624J5pmVQJdO4mcBpUgJkE
    // 7UkvZAlOI&signup=1&name=loi&email=stove+102test@gmail.com
    if (!currentUser.id) {
      currentUser = await UserApi.getUser()
    }
  } catch (error) {
    if (error.status === 401 && state.integrationPlatform === 'GoogleSlide') {
      cookie.deleteCookieGoogleSlide(AHA_TOKEN_COOKIE)
      return null
    }
    // A 401 is a verdict on the session, not on the connection; on a public page it must not raise the Disconnection modal.
    if (error.status !== 401) {
      commit('setIsServerConnectable', false)
    }

    utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'User > Load > Failed')
    throw error
  }

  currentUser.rememberSettings = currentUser.rememberSettings || {}
  if (currentUser.rememberSettings.sortColumn && currentUser.rememberSettings.sortOrder) {
    const currentSort = state.selections.selectedSortCriteria
    if (currentSort.sortColumn !== currentUser.rememberSettings.sortColumn ||
      currentSort.sortOrder !== currentUser.rememberSettings.sortOrder) {
      commit('setSelectedSortCriteria', {
        sortColumn: currentUser.rememberSettings.sortColumn,
        sortOrder: currentUser.rememberSettings.sortOrder,
      })
    }
  }
  if (currentUser.accountId) {
    dispatch('getCurrentTeamAction', currentUser.accountId)
  }
  if (currentUser.id && currentUser.plan !== 'white-label') {
    currentUser.isPlayingDemoEnabled = false
    if (!currentUser.playedPresentationIds ||
      (currentUser.playedPresentationIds && !currentUser.playedPresentationIds.length)) {
      currentUser.isPlayingDemoEnabled = true
    }
  }
  let payload = null
  try {
    payload = await PlanAPI.getMyPlan().then((res) => res.body)
    if (payload.subscription) {
      currentUser.cancel_at_period_end = payload.subscription.cancel_at_period_end
      if (payload.subscription.type) {
        currentUser.paymentSource = payload.subscription.metadata ? payload.subscription.metadata.paymentSource : null
        currentUser.plan = payload.subscription.type
      }
      currentUser.myPlan = payload
    }

    if (payload.subscription && payload.subscription.status === 'trialing') {
      commit('setIsTrialPlan', true)
    }

    if (payload && payload.subscription && payload.subscription.current_period_end) {
      currentUser.current_period_end = payload.subscription.current_period_end
      if ((moment(payload.subscription.current_period_end).isBefore(moment()))) {
        currentUser.plan = 'free'
      }
    }

    setTimeout(() => {
      dispatch('trialPlan/checkToShowTrialModalAction', currentUser)
    }, 1000)
  } catch (e) {
    reportMyPlanError(e, 'getCurrentUserActionV1')
  }
  const TIME_GO_LIVE = 'Mon Nov 13 2023 09:50:34 GMT+0700'
  try {
    if (currentUser) {
      const isUserMigrateToAu = (payload.subscription && payload.subscription.metadata) ?
        !!payload.subscription.metadata.billing_migration_id : false

      currentUser.isBeforeCurrentPeriodStart = currentUser.startDateFirstPlan ?
        moment(TIME_GO_LIVE).isBefore(moment(currentUser.startDateFirstPlan * 1000)) : !isUserMigrateToAu
    } else {
      currentUser.isBeforeCurrentPeriodStart = true
    }
  } catch (e) {
    Sentry.captureException(e, { extra: { message: 'Error plan before current period start' } })
  }

  commit('setCurrentUser', currentUser)
  commit('setPlan', currentUser.plan)

  // check current user language
  checkUserLanguage(currentUser, commit)

  if (!isFirstTimeLoadService) {
    isFirstTimeLoadService = true
    // handleUnleashStart kicks off the user-flag load. AHA-42358: wait for the
    // flags before fetching plan benefit so it routes to the correct endpoint
    // (see fetchCurrentUserAction for the full rationale). Settle on whichever
    // comes first — the readiness gate or the flag load itself — so we never hang
    // on a missed mutation. Kept non-blocking: getUserPlanBenefit was already
    // fire-and-forget here.
    const unleashStarted = handleUnleashStart(currentUser, state)
    Promise.race([
      store.dispatch('featureToggles/whenAuthFlagsReady'),
      unleashStarted.catch(() => {}),
    ])
      // AHA-42054: resolve the shared pricing lineup before plan benefit (see
      // fetchLineupThenPlanBenefit); self-bounded + safe-default, never blocks long.
      .then(() => fetchLineupThenPlanBenefit(currentUser, state))
      .catch(() => {})
  }

  // Legacy: the landing site still reads aha-user-id. Delete once headlesswp reads ahaUserId.
  cookie.setValue(userIdcookieName, currentUser.id)
  cookie.setValueByDomain(userIdcookieName, currentUser.id)

  Sentry.identifyUser(currentUser)
  sib.identifyUser(currentUser)
  zoho.identifyUser(currentUser)

  // TODO: should simplify with one funcion call like above
  mixpanelUtils.identify(currentUser)

  EventBus.$emit('handleAfterLoadedUser', currentUser)
  return currentUser
}

const getCurrentTeamAction = async ({ commit }, teamId) => {
  try {
    const currentAccount = await AccountApi.getAccountById(teamId)
    if (!currentAccount.id) currentAccount.id = 0
    commit('setCurrentAccount', currentAccount)
    mixpanelUtils.setTeamProps(currentAccount)
  } catch (error) {
    Sentry.captureException(error)
    if (error.status === 403) {
      commit('setCurrentAccount', {})
    }
  }
}

const setCurrentUserHasCreatedPresentationAction = async ({ commit, state }) => {
  const userHasCreatedPresentation = state.currentUser.personalInformation &&
    state.currentUser.personalInformation.hasCreatedPresentation

  if (!userHasCreatedPresentation) {
    const res = await UserApi.patchUserById(state.currentUser.id, {
      personalInformation: {
        ...state.currentUser.personalInformation,
        hasCreatedPresentation: true,
      },
    })

    const patchedUser = {
      ...state.currentUser,
      ...res.body,
    }

    commit('setCurrentUser', patchedUser)
  }
}

const updateCurrentUserAction = async ({ commit }, payload) => {
  commit('updateCurrentUser', payload)
}

const patchCurrentUserAction = async ({ commit, state }, payload) => {
  await UserApi.patchUserById(payload.userId, payload)
  if (!payload.personalInformation) return commit('updateCurrentUser', payload)
  // The API merges personalInformation keys, so the local copy must too or a partial patch drops the rest.
  return commit('updateCurrentUser', {
    ...payload,
    personalInformation: { ...state.currentUser?.personalInformation, ...payload.personalInformation },
  })
}

const setUpABTestPlanUpgradeEditorAction = async ({ commit }) => {
  const resGetABTest = await callApiGetABTest(AB_TEST.PLAN_UPGRADE_EDITOR)

  if (!resGetABTest || !resGetABTest.body) {
    return false
  }

  const { testCode, variant } = resGetABTest.body
  const hasABTestData = testCode && variant

  if (!hasABTestData) {
    return false
  }

  const payload = {}
  payload[AB_TEST.PLAN_UPGRADE_EDITOR] = variant
  commit('updateCurrentABTest', payload)

  return payload
}

const getLimitationPresentationAction = async ({ commit }, presentationId) => {
  const resp = await PlanBenefitAPI.getUserLimitationPresentation(presentationId)
  commit('setLimitationPresentation', resp)
}

const getServedSlideLimitsAction = async ({ commit }, presentationId) => {
  try {
    const slideLimits = await PlanBenefitAPI.getServedSlideLimits(presentationId)
    commit('setPresentationSlideLimits', slideLimits)
    commit('setPresentationSlideLimitsPresentationId', presentationId)
  } catch (err) {
    commit('setPresentationSlideLimits', null)
    commit('setPresentationSlideLimitsPresentationId', presentationId)
  }
}

const updateUserRememberSettingAction = async ({ commit, state }, payload) => {
  commit('updateCurrentUser', {
    rememberSettings: {
      ...state.currentUser.rememberSettings,
      ...payload,
    },
  })

  UserApi.updateSettings(payload)
}

const fetchCurrentUser = async ({ state, commit }) => {
  try {
    // use getUserWithCache to batch
    // adjacent in-flight requests
    // this is necessary since getCurrentUserAction
    // is called twice in some page
    return UserApi.getUserWithCache({ maxAgeInSeconds: API_CACHE_MAX_AGE_SECONDS })
  } catch (error) {
    if (error.status === 401 && state.integrationPlatform === 'GoogleSlide') {
      cookie.deleteCookieGoogleSlide(AHA_TOKEN_COOKIE)
      return null
    }
    commit('setIsServerConnectable', false)

    utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'User > Load > Failed')
    throw error
  }
}

const fetchCurrentPlan = async () => {
  try {
    // catch adjacent in-flight requests
    const { body: payload } = await PlanAPI.getMyPlanWithCache({ maxAgeInSeconds: API_CACHE_MAX_AGE_SECONDS })
    const currentPlan = {}
    if (payload.subscription) {
      currentPlan.cancel_at_period_end = payload.subscription.cancel_at_period_end
      if (payload.subscription.type) {
        currentPlan.paymentSource = payload.subscription.metadata ? payload.subscription.metadata.paymentSource : null
        currentPlan.plan = payload.subscription.type
      }
      currentPlan.myPlan = payload
    }

    if (payload && payload.subscription && payload.subscription.current_period_end) {
      currentPlan.current_period_end = payload.subscription.current_period_end
      if ((moment(payload.subscription.current_period_end).isBefore(moment()))) {
        currentPlan.plan = 'free'
      }
    }

    return currentPlan
  } catch (e) {
    reportMyPlanError(e, 'fetchCurrentPlan')
    return null
  }
}

const fetchUserPlanBenefit = async () => {
  try {
    // catch adjacent in-flight requests
    return PlanBenefitAPI.getUserPlanBenefitWithCache({ maxAgeInSeconds: API_CACHE_MAX_AGE_SECONDS })
  } catch (err) {
    Sentry.captureException(err)
    return null
  }
}

const handleUserAndPlanLoaded = ({ commit, state, dispatch }, { planBenefit, ...currentUser }) => {
  const subscription = currentUser?.myPlan?.subscription
  if (subscription && subscription.status === 'trialing') {
    commit('setIsTrialPlan', true)
  }
  setTimeout(() => {
    dispatch('trialPlan/checkToShowTrialModalAction', currentUser)
  }, 1000)

  currentUser.rememberSettings = currentUser.rememberSettings || {}
  if (currentUser.rememberSettings.sortColumn && currentUser.rememberSettings.sortOrder) {
    const currentSort = state.selections.selectedSortCriteria
    if (currentSort.sortColumn !== currentUser.rememberSettings.sortColumn ||
      currentSort.sortOrder !== currentUser.rememberSettings.sortOrder) {
      commit('setSelectedSortCriteria', {
        sortColumn: currentUser.rememberSettings.sortColumn,
        sortOrder: currentUser.rememberSettings.sortOrder,
      })
    }
  }
  if (currentUser.accountId) {
    dispatch('getCurrentTeamAction', currentUser.accountId)
  }
  if (currentUser.id && currentUser.plan !== 'white-label') {
    currentUser.isPlayingDemoEnabled = false
    if (!currentUser.playedPresentationIds ||
      (currentUser.playedPresentationIds && !currentUser.playedPresentationIds.length)) {
      currentUser.isPlayingDemoEnabled = true
    }
  }

  const TIME_GO_LIVE = 'Mon Nov 13 2023 09:50:34 GMT+0700'
  try {
    if (currentUser) {
      const isUserMigrateToAu = subscription?.metadata ?
        !!subscription.metadata.billing_migration_id : false

      currentUser.isBeforeCurrentPeriodStart = currentUser.startDateFirstPlan ?
        moment(TIME_GO_LIVE).isBefore(moment(currentUser.startDateFirstPlan * 1000)) : !isUserMigrateToAu
    } else {
      currentUser.isBeforeCurrentPeriodStart = true
    }
  } catch (e) {
    Sentry.captureException(e, { extra: { message: 'Error plan before current period start' } })
  }

  commit('setCurrentUser', currentUser)
  commit('setPlan', currentUser.plan)
  commit('setPlanBenefit', planBenefit)
  // check current user language
  checkUserLanguage(currentUser, commit)

  dispatch('featureToggles/initializeUserFeatureContext', {
    currentUser,
    presenterLanguage: state.presenterLocale,
  })
  if (store.getters['featureToggles/canUseFlagCohorts']) {
    startFlagCohortRecording(store, UserApi).catch(() => {})
  }

  Sentry.identifyUser(currentUser)
  sib.identifyUser(currentUser)
  zoho.identifyUser(currentUser)

  // TODO: should simplify with one funcion call like above
  mixpanelUtils.identify(currentUser)

  EventBus.$emit('handleAfterLoadedUser', currentUser)
}

const fetchCurrentUserAction = async ({
  commit, state, dispatch,
}) => {
  const [currentUser, currentPlan] = await Promise.all([
    fetchCurrentUser({ state, commit }),
    fetchCurrentPlan(),
  ])

  if (!currentUser) return null

  // AHA-42358: load user-scoped feature flags BEFORE plan benefit. Baking plan
  // benefit depends on user-scoped flags (e.g. the essential→pro override reads
  // canRemoveEssentialPlan / canUsePriceAlignmentFix below); fetching it in
  // parallel with the user — as before — evaluated those flags before Unleash had
  // loaded, so they defaulted off. We kick off the user-flag load and wait for it
  // before fetching plan benefit. The flag fetch is coalesced, so the dispatch in
  // handleUserAndPlanLoaded reuses this same run.
  //
  // We settle on whichever comes first: the reusable readiness gate
  // (whenAuthFlagsReady — also usable by other load-time features) OR the flag
  // load promise itself. Racing the actual operation means we never hang on a
  // missed setAuthFlag mutation, and whenAuthFlagsReady's own timeout guards the
  // case where Unleash is unreachable (→ plan benefit falls back to legacy).
  const userFlagsLoaded = dispatch('featureToggles/initializeUserFeatureContext', {
    currentUser, presenterLanguage: state.presenterLocale,
  }, { root: true }).catch(() => {})
  await Promise.race([
    dispatch('featureToggles/whenAuthFlagsReady', {}, { root: true }),
    userFlagsLoaded,
  ])
  if (store.getters['featureToggles/canUseFlagCohorts']) {
    Object.assign(currentUser, await knownFlagCohortsOf(currentUser, state.currentUser))
  }
  // AHA-42054: resolve the shared pricing-lineup read — the single source of truth
  // for whether Essential is hidden (the presenter reuses the pricing page's
  // server-resolved variant). Coalesced + safe default inside the action. Resolved
  // HERE, AFTER whenAuthFlagsReady, because the lineup read depends on the loaded
  // user context. Plan benefit's `*_planName` override reads the lineup result, so
  // the lineup must settle before we fetch + bake plan benefit below.
  await dispatch('pricingLineup/fetchPricingLineup', null, { root: true }).catch(() => {})

  const planBenefit = await fetchUserPlanBenefit()
  const currentUserWithPlan = { ...currentUser, ...currentPlan, planBenefit }

  // Legacy cookie, see above.
  cookie.setValue(userIdcookieName, currentUserWithPlan.id)
  cookie.setValueByDomain(userIdcookieName, currentUserWithPlan.id)
  dispatch('handleUserAndPlanLoaded', currentUserWithPlan)
  return currentUserWithPlan
}

const getCurrentUserAction = async ({ dispatch }, payload) => {
  if (flags.canUseCacheProfileOnLoad) {
    return dispatch('loadingPage/fetchAndSaveCurrentUserAction', payload, { root: true })
  }
  return dispatch('getCurrentUserActionV1', payload)
}

// Auth-success lifecycle hooks, fired at the end of the sign-up (handleRegisterSuccess)
// and log-in (LoginV3.login) flows respectively. They carry no logic of their own —
// each is purely a signal that feature plugins subscribe to (store.subscribeAction)
// and react to by doing their own post-auth navigation however they want (push,
// replace, external redirect...), so the auth methods stay free of per-feature
// routing branches. Kept as two distinct hooks so subscribers can tell sign-up from
// log-in; a subscriber that treats them the same (e.g. marketplace-redeem) just
// listens to both.
const signUpSuccessAction = async (_context, payload) => payload
const logInSuccessAction = async (_context, payload) => payload

// Exported so the my-plan failure classification can be unit tested directly,
// without driving it through the full getCurrentUserActionV1/
// fetchCurrentUserAction dependency graph.
export { isExpectedMyPlanFailure, reportMyPlanError }

export default {
  getCurrentUserActionV1,
  getCurrentUserAction,
  fetchCurrentUserAction,
  handleUserAndPlanLoaded,
  setCurrentUserAction,
  updateUserAfterCreatingTeamAction,
  setPlan,
  setCurrentUserHasCreatedPresentationAction,
  updateCurrentUserAction,
  patchCurrentUserAction,
  updateCurrentABTestAction,
  getCurrentTeamAction,
  setUpABTestPlanUpgradeEditorAction,
  getLimitationPresentationAction,
  getServedSlideLimitsAction,
  updateUserRememberSettingAction,
  signUpSuccessAction,
  logInSuccessAction,
}
